import Utils from "../../../../../../../../core/services/utils/utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {normalizeExactFormTitleKey} from "./lookup-stores/form-title-lookup-store";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

const collapseWhitespace = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

const resolveFormTitleKeyFromCell = (formTitleCell) => {
    if (formTitleCell == null) {
        return "";
    }
    if (typeof formTitleCell === "string") {
        return collapseWhitespace(formTitleCell);
    }
    return collapseWhitespace(formTitleCell?.title);
};

const parseIsRequiredToFill = (raw) => {
    if (raw === undefined || raw === null || String(raw).trim() === "") {
        return false;
    }
    if (typeof raw === "boolean") {
        return raw;
    }
    const parsed = Utils.tryConvertStringToBoolean(raw);
    return parsed === undefined ? undefined : parsed;
};

/**
 * Assigns Biz Forms to operator parts by part number and form title.
 */
export class ProductFormAssignmentImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {{ allForms?: Array<{ id: string, title?: string }>, formsApi?: * }} [options]
     */
    async run(mappedData, isFinalRun = false, options = {}) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];
        const {allForms = [], formsApi} = options;

        /** @type {{index: number, item: *, partNoUpper: string, formTitleTrimmed: string, formTitleKey: string, isRequired: boolean|undefined, codes: string[]}[]} */
        const rowState = rows.map((item, index) => {
            const formTitleTrimmed = resolveFormTitleKeyFromCell(item?.formTitle);
            const isRequiredParsed = parseIsRequiredToFill(item?.isRequiredToFill);
            return {
                index,
                item,
                partNoUpper: toPartNumberUpper(item?.partNumber),
                formTitleTrimmed,
                formTitleKey: normalizeExactFormTitleKey(formTitleTrimmed),
                isRequired: isRequiredParsed,
                codes: [],
            };
        });

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (!rs.partNoUpper || !rs.formTitleTrimmed) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
            if (
                rs.item.isRequiredToFill !== undefined &&
                rs.item.isRequiredToFill !== null &&
                String(rs.item.isRequiredToFill).trim() !== "" &&
                rs.isRequired === undefined
            ) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
        }

        const uniqueParts = [
            ...new Set(
                rowState
                    .filter((r) => r.item && r.partNoUpper && r.formTitleTrimmed)
                    .map((r) => r.partNoUpper),
            ),
        ].sort();

        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.partNoUpper || !rs.formTitleTrimmed) {
                continue;
            }
            if (!partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        const uniqueFormTitles = [
            ...new Set(
                rowState
                    .filter((r) => r.item && r.formTitleTrimmed)
                    .map((r) => r.formTitleTrimmed),
            ),
        ];

        /** @type {Record<string, *>} */
        let formIds = {};
        if (uniqueFormTitles.length > 0) {
            const formBundle = await this.ctx.fetchFormInfoByTitles(uniqueFormTitles, allForms, formsApi);
            formIds = formBundle.formIds;
        }

        for (const rs of rowState) {
            if (!rs.item || !rs.formTitleKey) {
                continue;
            }
            if (!formIds[rs.formTitleKey]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_FORM);
            }
        }

        const uniqueResolvedPartIds = [
            ...new Set(
                rowState
                    .filter(
                        (r) =>
                            r.item &&
                            r.partNoUpper &&
                            r.formTitleTrimmed &&
                            !r.codes.includes(PreventionInvalidityCode.INVALID_PART) &&
                            partIds[r.partNoUpper],
                    )
                    .map((r) => partIds[r.partNoUpper]),
            ),
        ];

        if (uniqueResolvedPartIds.length > 0) {
            await this.ctx.fetchPartFormAssociationsByPartIds(uniqueResolvedPartIds);
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {
                        partId: null,
                        formId: null,
                        partFormId: null,
                        operation: "add",
                        partNumber: null,
                        formTitle: null,
                        isRequired: false,
                    };
            }

            const rs = rowState[index];
            const partId = partIds[rs.partNoUpper];
            const formId = formIds[rs.formTitleKey];
            const isRequired = rs.isRequired === undefined ? false : rs.isRequired;
            const formDisplayTitle =
                typeof item.formTitle === "string" ? item.formTitle : item.formTitle?.title ?? item.formTitle;
            const existingAssociation =
                partId != null && formId != null
                    ? this.ctx.getPartFormAssociation(partId, formId)
                    : null;

            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        formId: formId ?? null,
                        partFormId: existingAssociation?.partFormId ?? null,
                        operation: existingAssociation?.partFormId != null ? "update" : "add",
                        partNumber: item.partNumber,
                        formTitle: formDisplayTitle,
                        isRequired,
                    };
            }

            return {
                partId,
                formId,
                partFormId: existingAssociation?.partFormId ?? null,
                operation: existingAssociation?.partFormId != null ? "update" : "add",
                partNumber: toPartNumberUpper(item?.partNumber),
                formTitle: formDisplayTitle,
                isRequired,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
